import { Injectable, ErrorHandler } from "@angular/core";
import { DialogService } from "primeng/dynamicdialog";
import { ErrorDialogComponent, ErrorDialogData } from "./error-dialog/error-dialog.component";
import { ApolloError } from "@apollo/client/errors";
import { Router } from "@angular/router";
import { ToastService } from "../toast/toast.service";
import { LogErrorGQL, LogErrorMutation } from "../graphql/gql";
import { PUBLIC_ROUTES } from "../app-routing.module";

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  constructor(
    private dialogService: DialogService,
    private router: Router,
    private toastService: ToastService,
    private logErrorGQL: LogErrorGQL
  ) {}

  handleError(error: any) {
    if (error instanceof ApolloError) {
      const error_codes = error.graphQLErrors.map(graphQLError => graphQLError.extensions?.['code']);

      // Redirect to login if authentication failed, but not if user is on a public page
      if(error_codes.includes('authentication_failed')) {
        const currentUrl = this.router.url;
        const targetUrl = window.location.pathname;
        const isOnPublicRoute = PUBLIC_ROUTES.some(route =>
          currentUrl.startsWith(route) || targetUrl.startsWith(route)
        );

        if (!isOnPublicRoute && !this.router.isActive('/login', { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' })) {
          this.toastService.show('info', 'Authentication failed. Please log in again.');
          this.router.navigate(['/login']);
        }
        return;
      }
      
      if(error_codes.includes('authorization_failed')) {
        this.toastService.show('error', $localize`:global error handler|authorization failed message:You do not have permission to perform this action.`);
        return;
      }
    }
    
    const errorUUID = crypto.randomUUID();

    console.error('Error occurred:', { uuid: errorUUID }, error);

    if (!this.rateLimitExceeded('sendErrorRateLimit', 60 * 60, 50)) {
      this.logErrorGQL.mutate({
        uuid: errorUUID,
        route: this.router.url,
        message: error.message,
        name: error.name,
        stackTrace: error.stack
      }, { errorPolicy: 'ignore' }).subscribe();
    }

    this.dialogService.open<ErrorDialogComponent, ErrorDialogData>(
      ErrorDialogComponent,
      {
        header: 'Error',
        modal: true,
        closable: true,
        contentStyle: { overflow: 'auto' },
        data: { errorCode: errorUUID }
      }
    )
  }

  /**
   * Simple rate limit function
   * 
   * Checks if the rate limit has been exceeded for a specific action.
   * 
   * @param localStorageKey The key used to store the rate limit information in local storage.
   * @param timeWindowSeconds The time window (in seconds) for which to check the rate limit.
   * @param maxCount The maximum number of allowed actions within the time window.
   * @returns True if the rate limit has been exceeded, false otherwise.
   */
  private rateLimitExceeded(localStorageKey: string, timeWindowSeconds: number, maxCount: number): boolean {
    let rateLimit: { count: number, timestamp: number };
    let rateLimitString = localStorage.getItem(localStorageKey);

    const now = Date.now();

    if (rateLimitString) {
      rateLimit = JSON.parse(rateLimitString);
    } else {
      rateLimit = { count: 0, timestamp: now };
    }

    const timeSinceDelta = now - rateLimit.timestamp;

    if (timeSinceDelta < (timeWindowSeconds * 1000)) {
      rateLimit.count++;
    } else {
      rateLimit.timestamp = now;
      rateLimit.count = 1;
    }

    localStorage.setItem(localStorageKey, JSON.stringify(rateLimit));

    return rateLimit.count > maxCount;
  }
}